/**
 * SquadLeaderboardSearch — plain search input for the squad leaderboard.
 *
 * Filters only the list rows beneath the podium; the podium top-3 stays visible.
 */

import React, { useMemo } from 'react';
import { Platform, Pressable, TextInput, View } from 'react-native';
import { Search, X } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getSquadLeaderboardSearchStyles } from './SquadLeaderboardSearch.styles';

interface Props {
  value: string;
  onChangeText: (next: string) => void;
  placeholder: string;
  accessibilityLabel: string;
}

export const SquadLeaderboardSearch: React.FC<Props> = ({
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
}) => {
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const styles = useMemo(
    () => getSquadLeaderboardSearchStyles(theme, isDark),
    [theme, isDark],
  );

  return (
    <View style={styles.outer}>
      <View style={styles.pill}>
        <View style={styles.iconLeading}>
          <Search size={16} color={theme.primary} strokeWidth={2.2} />
        </View>
        <TextInput
          style={styles.input}
          placeholder={placeholder}
          placeholderTextColor={isDark ? theme.gray[500] : theme.gray[600]}
          value={value}
          onChangeText={onChangeText}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
          underlineColorAndroid="transparent"
          accessibilityLabel={accessibilityLabel}
        />
        {value.length > 0 ? (
          <Pressable
            onPress={() => onChangeText('')}
            style={({ pressed, hovered }) => [
              styles.clearButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.pressed,
              hovered && Platform.OS === 'web' && styles.clearButtonHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            hitSlop={6}
          >
            <X size={14} color={theme.gray[600]} strokeWidth={2.4} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};
